import { Ray, Sphere, Vector3 } from "three";

// Exposure from what's on screen, like a camera's meter, instead of from
// which way the camera points: a grid of rays finds the Earth, and each hit
// reads how directly the sun lights it (a stand-in for its brightness).
//   - Average metering (centre weighted): exposure = K / (mean + E), which
//     gives DAY_EXPOSURE for a full sunlit disc (mean 2/3) and NIGHT_EXPOSURE
//     for a fully dark one.
//   - Highlight protection: once sunlit ground covers a fair part of the
//     view, exposure is capped so it doesn't blow out (a sliver of dayside
//     at the limb may still clip, as in a real night photo).
//   - Adaptation: exposure eases towards the target in log space, like an eye.

export const DAY_EXPOSURE = 2.5;
export const NIGHT_EXPOSURE = 8.0;
const FULL_DISC_MEAN = 2 / 3;
const E = (DAY_EXPOSURE * FULL_DISC_MEAN) / (NIGHT_EXPOSURE - DAY_EXPOSURE);
const K = NIGHT_EXPOSURE * E;
// Exposure x sun cosine allowed for the brightest sunlit ground, and the
// sunlit share of the view at which the cap applies fully. Low sun is capped
// harder than its cosine suggests (1.4, not the noon 2.0) because the grazing
// air near the terminator adds bright haze.
const HIGHLIGHT_LIMIT = 1.4;
const PROTECT_AT = 0.1;
const ADAPT_SECONDS = 0.35;
const COLS = 16;
const ROWS = 10;

const ray = new Ray();
const sphere = new Sphere();
const hit = new Vector3();
const ndc = new Vector3();

// Mean sun cosine (centre weighted), sunlit share and bright end (90th
// percentile) of the Earth on screen; null when no ray hits it.
export function meterEarth(camera, radius, sunDir) {
  sphere.set(new Vector3(), radius);
  let sum = 0;
  let weights = 0;
  const lit = [];
  let hits = 0;
  for (let j = 0; j < ROWS; j++) {
    for (let i = 0; i < COLS; i++) {
      const x = ((i + 0.5) / COLS) * 2 - 1;
      const y = ((j + 0.5) / ROWS) * 2 - 1;
      ray.origin.setFromMatrixPosition(camera.matrixWorld);
      ray.direction.copy(ndc.set(x, y, 0.5).unproject(camera)).sub(ray.origin).normalize();
      if (!ray.intersectSphere(sphere, hit)) continue;
      hits++;
      const cos = Math.max(0, hit.normalize().dot(sunDir));
      const w = 1 - 0.5 * (x * x + y * y) / 2;
      sum += w * cos;
      weights += w;
      if (cos > 0.05) lit.push(cos);
    }
  }
  if (!hits) return null;
  lit.sort((a, b) => a - b);
  return {
    mean: sum / weights,
    litShare: lit.length / hits,
    bright: lit.length ? lit[Math.floor(lit.length * 0.9)] : 0,
  };
}

export function targetExposure(meter) {
  if (!meter) return DAY_EXPOSURE; // not looking at the Earth (e.g. the sunlit Moon)
  const average = K / (meter.mean + E);
  const protect = Math.min(1, meter.litShare / PROTECT_AT);
  const cap = meter.bright > 0 ? HIGHLIGHT_LIMIT / meter.bright : NIGHT_EXPOSURE;
  const capped = NIGHT_EXPOSURE + (Math.min(cap, NIGHT_EXPOSURE) - NIGHT_EXPOSURE) * protect;
  // Never below the day exposure, which is set for sunlit views.
  return Math.max(DAY_EXPOSURE, Math.min(average, capped));
}

// Eases exposure towards the target; call once per frame.
export function createAdaptation() {
  let current = null;
  let last = 0;
  return (target, now = performance.now()) => {
    if (current === null) {
      current = target;
    } else {
      const dt = Math.min(0.25, (now - last) / 1000);
      const k = 1 - Math.exp(-dt / ADAPT_SECONDS);
      current = Math.exp(Math.log(current) + (Math.log(target) - Math.log(current)) * k);
    }
    last = now;
    return current;
  };
}
